import type { ReactElement } from "react";
import { useState } from "react";
import { AppFooter } from "@/app/layout/app-footer";
import { PublicHeader } from "@/app/layout/public-header";
import { PublicBrandHeader } from "@/shared/components/public-brand-header";
import { Card } from "@/shared/components/ui";
import {
  captureInvitationProofFromLocation,
  clearCapturedInvitationProof,
} from "./invitation-proof-fragment";

/**
 * Admin-host route for `/register/invitation` (CLMS-113). The UI contract
 * ("Authentication compatibility") forbids completing public registrations
 * from the admin realm: this page renders generic support/sign-out guidance
 * only — never the registration form — and never calls lookup. Any proof
 * fragment is removed from the URL/history and immediately discarded so it
 * cannot linger on the admin host.
 */
export function RegistrationInvitationAdminRealmPage(): ReactElement {
  // Fragment scrub happens during first render, before first paint — the
  // same render-time capture the public page uses — and the proof is
  // discarded immediately instead of being retained. Ticket-scoped: scrubs
  // exactly what THIS render staged (a null ticket resets only a proof-less
  // memoization) without erasing a capture staged by a coexisting public
  // flow.
  useState(() => {
    const capture = captureInvitationProofFromLocation();
    clearCapturedInvitationProof(capture.ticket);
    // This page also mirrors `/register/invitation/recovery`: a recovery
    // return carries `?ref=<recoveryReference>`, which the standard-realm
    // page scrubs from the address bar/history on mount (011-15 UI contract
    // L183). Apply the same scrub here so the reference never lingers on
    // the admin host either.
    if (window.location.search !== "") {
      window.history.replaceState(null, "", window.location.pathname);
    }
    return null;
  });

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      {/* FR-009.5: same chrome/card/brand composition as the public
          registration surfaces so admin-realm guidance reads as the same
          branded surface. */}
      <PublicHeader />
      <main className="clm-page-gutter flex flex-1 items-center justify-center bg-clm-primary-blue py-12">
        <Card className="w-full max-w-lg border-0 bg-white px-2 py-8 shadow-md sm:px-8">
          <div className="px-6">
            <PublicBrandHeader className="mb-8" />
            <h1
              tabIndex={-1}
              className="text-center text-2xl font-light text-clm-dark-gray focus:outline-none"
            >
              Registration isn&apos;t available here
            </h1>
            <p className="mt-2 text-base text-gray-600">
              Registration links can&apos;t be used in this application. Sign out of this
              administrative session, then open your registration link again. Contact support if you
              need help with your registration.
            </p>
          </div>
        </Card>
      </main>
      <AppFooter />
    </div>
  );
}
